import React, { useEffect, useState } from "react";
import client, { apiError } from "@/lib/api";
import { CategoriesResponse, Debt } from "@/lib/types";
import { money } from "@/lib/format";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";

interface Props {
  categories: CategoriesResponse | null;
  onCreated: () => void;
  fab?: boolean;
}

const RegisterMovementDialog: React.FC<Props> = ({ categories, onCreated, fab }) => {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ type: "outflow", amount: "", description: "", date: "", category: "unclassified" });
  const [debts, setDebts] = useState<Debt[]>([]);
  const [debtPay, setDebtPay] = useState({ debt_id: "", capital: "", interes: "", comision: "", recargo: "" });
  const isDebt = form.type === "debt";
  const num = (v: string) => parseFloat(v) || 0;
  const debtTotal = num(debtPay.capital) + num(debtPay.interes) + num(debtPay.comision) + num(debtPay.recargo);

  useEffect(() => {
    if (open && isDebt) {
      client.get("/debts").then((r) => setDebts(r.data.filter((d: Debt) => d.status !== "paid")));
    }
  }, [open, isDebt]);

  const reset = () => {
    setForm({ type: "outflow", amount: "", description: "", date: "", category: "unclassified" });
    setDebtPay({ debt_id: "", capital: "", interes: "", comision: "", recargo: "" });
  };

  const submitDebt = async () => {
    if (!debtPay.debt_id || debtTotal <= 0) {
      toast.error("Selecciona la deuda y captura un monto mayor a cero");
      return;
    }
    setSaving(true);
    try {
      await client.post(`/debts/${debtPay.debt_id}/payments`, {
        capital: num(debtPay.capital),
        interes: num(debtPay.interes),
        comision: num(debtPay.comision),
        recargo: num(debtPay.recargo),
        note: form.description,
        date: form.date ? `${form.date}T12:00:00Z` : undefined,
      });
      toast.success("Pago de deuda registrado");
      setOpen(false);
      reset();
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const submit = async () => {
    if (isDebt) return submitDebt();
    if (!form.description || !form.amount) {
      toast.error("Descripción y monto son obligatorios");
      return;
    }
    setSaving(true);
    try {
      await client.post("/cash/movements", {
        type: form.type,
        amount: parseFloat(form.amount),
        description: form.description,
        category: form.category,
        date: form.date ? `${form.date}T12:00:00.000Z` : undefined,
      });
      toast.success("Movimiento registrado");
      setOpen(false);
      reset();
      onCreated();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="register-movement-button"
        className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000]"
      >
        <Plus className="h-4 w-4" strokeWidth={2} />
        Registrar gasto / movimiento
      </button>
      {fab && (
        <button
          onClick={() => setOpen(true)}
          data-testid="register-movement-fab"
          title="Registrar gasto / movimiento"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#DD0000] px-5 py-3 text-sm font-medium text-white shadow-lg shadow-red-900/20 transition-transform hover:scale-105 hover:bg-[#B00000]"
        >
          <Plus className="h-4 w-4" strokeWidth={2} />
          Registrar
        </button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none" data-testid="register-movement-dialog">
          <DialogHeader>
            <DialogTitle>Registrar movimiento manual</DialogTitle>
            <DialogDescription>Captura gastos, ingresos, pagos de deuda u otros movimientos de tu caja.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Tipo</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="movement-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="outflow">Salida (gasto)</SelectItem>
                    <SelectItem value="inflow">Entrada (ingreso)</SelectItem>
                    <SelectItem value="debt">Pago de deuda (abono)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {isDebt ? (
                <div>
                  <Label className="text-xs text-slate-500">Deuda</Label>
                  <Select value={debtPay.debt_id} onValueChange={(v) => setDebtPay({ ...debtPay, debt_id: v })}>
                    <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="movement-debt-select">
                      <SelectValue placeholder="Selecciona la deuda" />
                    </SelectTrigger>
                    <SelectContent>
                      {debts.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.creditor} · {money(d.outstanding)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
              <div>
                <Label className="text-xs text-slate-500">Monto (MXN)</Label>
                <Input
                  type="number"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="movement-amount-input"
                  className="mt-1.5 rounded-none border-slate-300"
                />
              </div>
              )}
            </div>
            {isDebt && (
              <div className="grid grid-cols-2 gap-3 border-l-2 border-amber-300 bg-amber-50 p-3" data-testid="movement-debt-breakdown">
                {(["capital", "interes", "comision", "recargo"] as const).map((k) => (
                  <div key={k}>
                    <Label className="text-xs capitalize text-amber-800">{k === "interes" ? "Interés" : k === "comision" ? "Comisión" : k}</Label>
                    <Input
                      type="number"
                      value={debtPay[k]}
                      onChange={(e) => setDebtPay({ ...debtPay, [k]: e.target.value })}
                      data-testid={`movement-debt-${k}`}
                      className="mt-1 rounded-none border-amber-300 bg-white"
                    />
                  </div>
                ))}
                <div className="col-span-2 text-sm text-amber-900" data-testid="movement-debt-total">
                  Total del pago: <span className="font-medium tabular">{money(debtTotal)}</span>
                </div>
              </div>
            )}
            <div>
              <Label className="text-xs text-slate-500">{isDebt ? "Nota (opcional)" : "Descripción"}</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="movement-description-input"
                className="mt-1.5 rounded-none border-slate-300"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-500">Fecha</Label>
                <Input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="movement-date-input"
                  className="mt-1.5 rounded-none border-slate-300"
                />
              </div>
              {!isDebt && (
              <div>
                <Label className="text-xs text-slate-500">Categoría</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="mt-1.5 rounded-none border-slate-300" data-testid="movement-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unclassified">Sin clasificar</SelectItem>
                    {categories &&
                      Object.entries(categories.groups)
                        .filter(([g]) => g !== "pendientes" && g !== "deuda_financiamiento")
                        .map(([g, items]) => (
                          <SelectGroup key={g}>
                            <SelectLabel>{categories.group_labels[g]}</SelectLabel>
                            {items.map((it) => (
                              <SelectItem key={it.key} value={it.key}>
                                {it.label}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                  </SelectContent>
                </Select>
              </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button
              onClick={submit}
              disabled={saving}
              data-testid="movement-submit"
              className="flex items-center gap-2 rounded-none bg-[#DD0000] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#B00000] disabled:opacity-60"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin" />}
              Registrar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RegisterMovementDialog;
